import { type Spacings } from '@smart/web-foundations';
import classNames from 'classnames';
import type { ExtendWithCommonProps } from '../../types';
import { Flex } from '../flex/flex';
import { Text, type TextProps } from '../text/text';
import { ListItem } from './list-item';
import scss from './list.module.scss';

export type ListProps = ExtendWithCommonProps<
  Omit<TextProps, 'as'>,
  {
    /** HTML tag to render */
    as?: 'ol' | 'ul';
    /** Gap between items */
    gap?: Spacings;
  }
>;

const List = ({
  children,
  className,
  as = 'ul',
  gap = 200,
  ...props
}: ListProps) => {
  const classes = classNames(
    scss.list,
    {
      [scss.ol]: as === 'ul',
      [scss.ul]: as === 'ol',
    },
    className,
  );
  const Tag = as === 'ul' ? 'ul' : 'ol';
  return (
    <Text
      className={classes}
      margin={{ bottom: 400 }}
      padding={{ left: 400 }}
      {...props}
      asChild
    >
      <Tag className={className}>
        <Flex direction="column" gap={gap}>
          {children}
        </Flex>
      </Tag>
    </Text>
  );
};

/**
 * Use to create a list of items. Can render as an ordered or unordered list.
 *
 * Composed of:
 * - `<List.Item>` to add a single list item.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/components-list--docs
 */
const ListNamespace = Object.assign(List, {
  Item: ListItem,
});
export { ListNamespace as List };

List.displayName = 'List';
